import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ExternalLink, Loader2, CalendarClock } from 'lucide-react';
import { ReviewPostponeDialog } from '../components/ReviewPostponeDialog';
import { ReviewPostponementInfo } from '../components/ReviewPostponementInfo';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReviewDetailPage() {
  const { reviewId } = useParams();
  const [review, setReview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [postponing, setPostponing] = useState(false);
  const reload = useCallback(async () => {
    const { data } = await axios.get(`${API}/push/reviews/${encodeURIComponent(reviewId)}`, { withCredentials: true });
    setReview(data);
  }, [reviewId]);
  useEffect(() => {
    let active = true;
    setReview(null); setError('');
    axios.get(`${API}/push/reviews/${encodeURIComponent(reviewId)}`, { withCredentials: true })
      .then(({ data }) => { if (active) setReview(data); })
      .catch(() => { if (active) setError('La solicitud no está disponible o ya no tienes acceso.'); });
    return () => { active = false; };
  }, [reviewId]);
  const markRead = async () => {
    if (busy || !window.confirm('No cierra la Solicitud de Revisión. ¿Marcar este aviso como LEÍDO?')) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/me/pendientes-revision/${encodeURIComponent(reviewId)}/marcar-revisado`, {}, { withCredentials: true });
      setReview(previous => ({ ...previous, status: 'revisado' }));
      window.dispatchEvent(new CustomEvent('xenicos:refresh-pendientes'));
      window.dispatchEvent(new CustomEvent('xenicos:refresh-bell'));
    } catch { setError('No se pudo actualizar el aviso. Inténtalo de nuevo.'); }
    finally { setBusy(false); }
  };
  return <main className="max-w-3xl space-y-6" data-testid="review-detail-page">
    <Link to="/pendientes-revision" className="inline-flex items-center gap-2 text-sm" data-testid="review-detail-back"><ArrowLeft className="w-4 h-4" />Pendientes de revisión</Link>
    <h1 className="heading-1">Solicitud de revisión</h1>
    {error && <p role="alert" className="text-red-500 text-sm" data-testid="review-detail-error">{error}</p>}
    {!review && !error && <Loader2 className="animate-spin" data-testid="review-detail-loading" />}
    {review && <>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span data-testid="review-detail-requester">Solicitada por {review.requester_name}</span>
        <span data-testid="review-detail-date">{new Date(review.created_at).toLocaleString('es-ES')}</span>
        <span data-testid="review-detail-status">{review.status === 'pendiente' ? 'Pendiente' : 'Leída'}</span>
      </div>
      <p className="whitespace-pre-wrap [overflow-wrap:anywhere] leading-relaxed" data-testid="review-detail-note">{review.note || 'Sin nota adjunta'}</p>
      {review.deadline_at && <p className="text-sm" data-testid="review-detail-deadline">Vencimiento: {review.postponement?.due_date
        ? new Date(`${review.postponement.due_date}T00:00:00`).toLocaleDateString('es-ES') : new Date(review.deadline_at).toLocaleString('es-ES')}</p>}
      <ReviewPostponementInfo review={review} prefix="review-detail" />
      {review.postponement_history?.length > 1 && <details data-testid="review-detail-postponement-history">
        <summary className="cursor-pointer text-sm" data-testid="review-detail-history-toggle">Últimos aplazamientos</summary>
        {review.postponement_history.slice(0, -1).reverse().map(entry => <div key={entry.revision} className="py-3 border-b" data-testid={`postponement-history-${entry.revision}`}>
          <p className="text-sm">{new Date(`${entry.due_date}T00:00:00`).toLocaleDateString('es-ES')}</p>
          <ReviewPostponementInfo review={{ postponement: entry }} prefix={`history-${entry.revision}`} />
        </div>)}
      </details>}
      <div className="flex flex-wrap gap-3">
        {review.can_postpone && <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setPostponing(true)}
          data-testid="review-detail-postpone"><CalendarClock size={16} />Posponer</button>}
        {review.asset_id && <Link to={`/activos/${review.asset_id}`} className="btn-secondary inline-flex items-center gap-2" data-testid="review-detail-asset"><ExternalLink className="w-4 h-4" />Ver activo</Link>}
        {review.status === 'pendiente' && <button onClick={markRead} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="review-detail-read"><Check className="w-4 h-4" />{busy ? 'Guardando…' : 'LEÍDO'}</button>}
      </div>
      {postponing && <ReviewPostponeDialog item={review} onCancel={() => setPostponing(false)}
        onSaved={async () => { await reload(); setPostponing(false); }} />}
    </>}
  </main>;
}